import { ChangeEvent, ClickEvent, Image, Overlay, Panel, Tree, TreeNode } from "@mcleod/components";
import { MenuItem, ObjectFilterFunction, ObjectUtil } from "@mcleod/core";
import { ModelPermsTree } from "../models/ModelPermsTree";
import { AutogenLayoutPermissionsMain } from "./autogen/AutogenLayoutPermissionsMain";
import { PanelPermsGrant } from "./PanelPermsGrant";
import { PermissionsSource } from "./PermissionsSource";
import { PermsContainer } from "./PermsContainer";
import { PermsMenuTreeNode } from "./PermsMenuTreeNode";

interface PermMenuItem extends MenuItem {
    nestedLayoutPaths?: string[];
}

export class PermissionsMain extends AutogenLayoutPermissionsMain implements PermsContainer {
    panelGrant: PanelPermsGrant;
    permissionsSource: PermissionsSource;
    menuData: PermMenuItem;
    nonMenuData: PermMenuItem;
    actionPerms: PermMenuItem;
    apiPerms: PermMenuItem;
    treeMenuCasted: Tree<PermsMenuTreeNode>;

    override async onLoad() {
        this.savePermsButton.enabled = false;
        this.treeMenuCasted = this.treeMenu as Tree<PermsMenuTreeNode>;
        this.treeMenuCasted.selectedNodeBackgroundColor = "primary.lightest";
        this.treeMenuCasted.selectedNodeColor = "default"
        this.switchPermsSet.allowIndeterminate = true;
        this.switchPermsSet.checked = undefined;
        this.switchPermsSet.addChangeListener(() => this.populateTree());
        this.textSearchMenu.imagePre = new Image({ name: "funnel", color: "#9E9E9E", paddingLeft: 4, height: 28, width: 28 });
        await this.loadPerms();
        this.panelGrant = (this.layoutPermsGrant as PanelPermsGrant);
        this.panelGrant.permsContainer = this;
        this.panelGrant.scrollY = true;
        this.populateTree();
        this.addMountListener(() => this.textSearchMenu.focus());

        this.permissionsSource.addChangedDataCallback(hasChanged => {
            this.savePermsButton.enabled = hasChanged;
        });
    }

    async loadPerms() {
        await this.populatePermCollections();
        const menuData = await new ModelPermsTree().search();
        const response = menuData.modelRows[0];
        this.menuData = response.get("menu");
        this.nonMenuData = response.get("non_menu");
        this.actionPerms = response.get("actions").actions;
        this.apiPerms = response.get("api").api;
    }

    syncMenuTreeNodes() {
        this.treeMenu.getRootNode().recurseChildren(node => {
            if (node instanceof PermsMenuTreeNode) {
                node.syncUI();
            }
        });
    }

    async populatePermCollections() {
        this.permissionsSource = new PermissionsSource();
        await this.permissionsSource.loadPermissionsData();
    }

    textSearchMenuOnChange(_event: any) {
        this.populateTree();
    }

    treeMenuOnChange(event: ChangeEvent) {
        this.menuItemSelected();
    }

    menuItemSelected() {
        const items = this.treeMenuCasted.selectedNodes?.map(node => node.secureItem);
        this.panelGrant.setItemsBeingEdited(items);
    }

    populateTree() {
        this.panelGrant.clear();
        if (this.menuData == null)
            return;

        const rootChildren = [
            this.createHeaderNode("Menu items", this.menuData.items, false),
            this.createHeaderNode("Non-menu items", this.nonMenuData.items, false),
            this.createHeaderNode("Actions", this.actionPerms.items, true),
            this.createHeaderNode("API", this.apiPerms.items, true),
        ];
        for (let i = rootChildren.length - 1; i >= 0; i--)
            if (rootChildren[i].getChildCount() === 0)
                rootChildren.splice(i, 1);
        this.treeMenu.getRootNode().setChildren(rootChildren);
        if (this.filter != null)
            this.treeMenu.getRootNode().expandAll();
        this.treeMenu.selectedNode = null;
    }

    private createHeaderNode(text: string, items: PermMenuItem[], isAction: boolean) {
        let filtered = items;
        const filter = this.filter;
        if (filter != null) {
            filtered = ObjectUtil.deepCopy(filtered);
            filtered = ObjectUtil.filterRecursiveArrayWithFunction<PermMenuItem>(filtered, "items", filter);
        }
        const result = new TreeNode({ text, selectable: false });
        for (const item of filtered)
                this.addTreeNode(result, item, isAction);
        result.removeChildrenMatching(node => node.expandedImageName != null && node.getChildCount() === 0);
        this.addMenuItemContextMenu(result);
        return result;
    }

    private get filter(): ObjectFilterFunction<PermMenuItem>  {
        if (this.textSearchMenu.isEmpty() && this.switchPermsSet.checked == undefined) {
            return null;
        }
        return (item: PermMenuItem) => {
            let result = true;
            if (this.textSearchMenu.text.length > 0)
                result = item.caption?.toLowerCase().includes(this.textSearchMenu.text.toLowerCase());
            if (this.switchPermsSet.checked === false && result) {
                result = this.permissionsSource.getEntireScreenSecureItemRow(item.path) != null;
            } else if (this.switchPermsSet.checked === true && result) {
                const layoutPaths = new Set([item.path, ...item.nestedLayoutPaths ?? []]);
                result = this.permissionsSource.getSecureItemRows(row => {
                    return row.isFieldLevel() && layoutPaths.has(row.get("screen_class"));
                })?.length > 0;
            }
            return result;
        }
    }

    private addTreeNode(parent: TreeNode, item: PermMenuItem, isAction: boolean) {
        if (item.items == null) {
            const node = this.createMenuNode(item, isAction);
            parent.addChild(node);
        }
        else {
            const node = new TreeNode({ text: item.caption, expandedImageName: "checkboxIndeterminate", collapsedImageName: "addInBox", allowSelect: false, selectable: false });
            this.addMenuItemContextMenu(node);
            parent.addChild(node);
            for (const child of item.items)
                this.addTreeNode(node, child, isAction);
        }
    }

    private addMenuItemContextMenu(node: TreeNode) {
        node.component.contextMenuItems = [
            { text: "Secure all sub items", onClick: () => {
                const nodes = this.getPermsMenuTreeNodes(node);
                nodes.forEach(node => this.permissionsSource.addSecureItemRow(node.secureItem));
                this.treeMenu.selectedNodes = nodes;
            }},
            { text: "Unsecure all sub items", onClick: () => {
                const nodes = this.getPermsMenuTreeNodes(node);
                nodes.forEach(node => this.permissionsSource.deleteSecureItem(node.secureItem));
            }}
        ];

        node.component.addContextMenuCreationListener(() => {
            this.treeMenu.selectedNode = null;
            node.expanded = true;
            node.expandAll();
        });
    }

    private getPermsMenuTreeNodes(node: TreeNode): PermsMenuTreeNode[] {
        return node.getRecursiveChildNodes(node => node instanceof PermsMenuTreeNode) as PermsMenuTreeNode[];
    }

    private createMenuNode(item: PermMenuItem, isAction: boolean): PermsMenuTreeNode {
        const result  = new PermsMenuTreeNode(item.path, isAction, {
            title: item.caption,
            nestedLayoutPaths: item.nestedLayoutPaths,
            permissionsMain: this
        });
        return result;
    }

    /** This is an event handler for the onClick event of savePermsButton. */
    savePermsButtonOnClick(event: ClickEvent) {
        this.savePermsButton.busy = true;
        const savePromise = this.permissionsSource.post().then(() => {
            this.savePermsButton.enabled = false;
        }).finally(() => {
            this.savePermsButton.busy = false
        });
        this.addBusyOverlay(savePromise);
    }

    private addBusyOverlay(promise: Promise<any>) {
        const spinnerImage = new Image({
            id: "spinner",
            name: "spinner",
            rotate: true,
            color: "primary",
            height: 48,
            width: 48,
            style: {
                position: "absolute",
                top: "40%",
                left: "50%",
                transform: "translate(-50%, -50%)"
            }
        });

        const overlay = Overlay.showInOverlay(new Panel(), { greyedBackground: true, closeOnClickOff: false, coverPageHeader: false });
        overlay.overlayDiv.appendChild(spinnerImage._element);
        promise.finally(() => {
            Overlay.hideOverlay(overlay);
        });
    }
}
